import { Typography } from '@material-ui/core';
import { Inline, Stack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { useCustomerSupport } from 'shared/helpers/CustomerSupport';
import { VerifiedCarrierApplicationStatusDTO } from 'shared/modules/carrier-profile/data/VerifiedCarrierApplicationStatusDTO';
import { WebViewLink } from 'shared/routing/WebViewLinks';
import { useVerifiedCarrierApplicationStatus } from '../data/VerifiedCarrierApplicationStatusAPI';
import { FieldAdditionalMessages } from './AdditionalMessages';
import { VerificationStatusMessages } from './VerificationStatusMessages';
import { useFmcsaSync } from 'shared/modules/carrier-profile/core/FmcsaSyncUtils';
import { Sync } from '@material-ui/icons';
import { useEffect, useRef } from 'react';
import { trackCarrierProfileEvent } from 'shared/modules/carrier-profile/data/CarrierProfileAnalytics';

interface ReliabilityDeclinedReasonsProps {
  application: VerifiedCarrierApplicationStatusDTO['verified_carrier_application'];
}

function ReliabilityDeclinedReasons({
  application,
}: ReliabilityDeclinedReasonsProps) {
  const { showLauncherMessanger } = useCustomerSupport();
  const decliningReasons = application.declining_reasons?.reliability_score;
  const externalNotes = application.external_notes?.low_reliability;

  if (!decliningReasons?.length) {
    return null;
  }

  return (
    <FieldAdditionalMessages
      variant="error"
      listStyle="plain"
      messages={decliningReasons}
      externalNotes={externalNotes}
      actions={
        <Stack space="xxsmall">
          <Button
            size="small"
            fullWidth={isWebView}
            variant="neutral"
            onClick={() => {
              showLauncherMessanger();
            }}
          >
            Contact Support
          </Button>
        </Stack>
      }
    />
  );
}

interface AuthorityDeclinedReasonsProps {
  application: VerifiedCarrierApplicationStatusDTO['verified_carrier_application'];
  onSyncClicked: () => void;
}

function AuthorityDeclinedReasons({
  application,
  onSyncClicked,
}: AuthorityDeclinedReasonsProps) {
  const decliningReasons = application.reasons.operation;
  const externalNotes = application.external_notes?.usdot_certificate;

  const { fmcsaSync, isTimeElapsed, isPending } = useFmcsaSync();

  if (!decliningReasons.length) {
    return null;
  }

  return (
    <VerificationStatusMessages
      variant="error"
      title="Your verification application has been declined due to:"
      messages={decliningReasons}
      extendedMessages={{
        usdot_not_authorized_to_haul_motor_carriers: (
          <Typography>
            The USDOT is not authorized to haul motor vehicles, please update
            your MCS-150 form with FMCSA portal to adjust or add &quot;Motor
            Vehicle&quot; or &quot;Tow/Drive away&quot; cargo carried option.
          </Typography>
        ),
      }}
      externalNotes={externalNotes}
      additionalMessage={
        <Stack space="none">
          <Typography color="textPrimary">
            Once FMCSA restores your authority, we’ll resubmit it automatically.
            If you experience a delay, click &quot;Sync to FMCSA&quot; to check
            immediately.
          </Typography>
          <WebViewLink
            target="_blank"
            rel="noreferrer"
            href="https://support.superdispatch.com/hc/en-us/articles/40225296315539-How-to-Reactivate-an-Inactive-USDOT-or-MC-Number"
          >
            Learn more
          </WebViewLink>
        </Stack>
      }
      actions={
        <Inline verticalAlign="center">
          <Button
            startIcon={<Sync />}
            size="small"
            pending={isPending}
            disabled={!isTimeElapsed}
            fullWidth={isWebView}
            variant="neutral"
            onClick={() => {
              onSyncClicked();
              fmcsaSync();
            }}
          >
            Sync to FMCSA
          </Button>
          {!isTimeElapsed && (
            <Typography color="textSecondary">
              Recently synced. Wait 5m before syncing again.
            </Typography>
          )}
        </Inline>
      }
    />
  );
}

export function CarrierOverviewDecliningReasons() {
  const lastStatus = useRef<string>();
  const { data } = useVerifiedCarrierApplicationStatus();
  const application = data?.verified_carrier_application;
  const isDeclined = application?.status === 'declined';
  const isReliabilityScore = application?.reasons.reliability_score.length;
  const isAuthority = application?.reasons.operation.length;

  useEffect(() => {
    if (application && lastStatus.current) {
      trackCarrierProfileEvent({
        name: 'Carrier Synced with FMCSA',
        status_changed: application.status !== lastStatus.current,
        previous_status: lastStatus.current,
        new_status: application.status,
        has_authority_reasons: !!application.reasons.operation.length,
      });
      lastStatus.current = undefined;
    }
  }, [application, lastStatus]);

  if (isDeclined && isReliabilityScore) {
    return <ReliabilityDeclinedReasons application={application} />;
  }

  if (isDeclined && isAuthority) {
    return (
      <AuthorityDeclinedReasons
        application={application}
        onSyncClicked={() => {
          lastStatus.current = application.status;
        }}
      />
    );
  }

  return null;
}
